import { slopeEstimationPercentile, interceptEstimationPercentile, roundToPrecision } from './shared-util';
import { Log_WeighIn } from './data-model/log-weigh-in';
import { DateTime } from 'luxon';

/**
 * How many days back the weight trend line is read. Reading a week back is
 * "similar to average of last two weeks" when there are two weeks of data.
 */
export const TREND_READ_DAYS_AGO = 7;

/**
 * #6067: the fewest caliper (or tape) readings in the 14-day window for the body-fat trend to be
 * estimated at all. A 5-day measuring rhythm gives exactly this many. The plan check and the
 * Body Composition overview's charts (#5456) both read this one number.
 */
export const MIN_BODY_FAT_SAMPLES_FOR_TREND = 3;

export interface WeightEstimationResult {
  /** Estimated weight on the trend line TREND_READ_DAYS_AGO (7) days ago, or at the earliest sample if that is newer (kg) */
  estimatedBW_kg: number;
  /** Weekly weight change rate (kg/week) */
  weightSlope_kgwk: number;
}

/**
 * Core Theil-Sen estimation from pre-converted weight samples.
 * This is the shared calculation used by plan-update.ts and other places.
 *
 * The line is read at 7 days ago, or at the earliest sample when every sample is newer than
 * that. Reading before the earliest sample would extrapolate to a weight the user never had:
 * the plan engine only counts weigh-ins since the last nutrition adjustment, so for a few days
 * after one, 5 daily weigh-ins start only 4 to 6 days back (df-abc953b77752; the Goal card's
 * copy of the same problem was #6050).
 * @param weightSamples Array of {x: daysFromEnd (negative), y: weight_kg}
 * @returns Estimation result with weight and slope, or undefined if insufficient data (< 2 samples)
 */
export function estimateWeightFromSamples(weightSamples: { x: number; y: number }[]): WeightEstimationResult | undefined {
  // need at least 2 samples to calculate trend
  if (weightSamples.length < 2) return undefined;

  const slope = slopeEstimationPercentile(weightSamples, 0.5);
  const intercept = interceptEstimationPercentile(weightSamples, slope, 0.5);

  // read 7 days ago, unless that lies before the earliest sample
  const earliestDay = Math.min(...weightSamples.map(s => s.x));
  const readDay = Math.max(-TREND_READ_DAYS_AGO, earliestDay);

  return {
    // use weight 7 days ago - this is similar to average of last two weeks
    estimatedBW_kg: slope * readDay + intercept,
    weightSlope_kgwk: slope * 7,
  };
}

/**
 * Minimum number of weigh-ins needed in the LAST 7 DAYS before we trust a
 * weekly weight trend. This is the "optimal" bar the plan engine uses in
 * `analyzeWeightIns` (shared/src/plan-update.ts), which imports this constant.
 */
export const MIN_WEIGHINS_LAST_7D_FOR_TREND = 5;

/**
 * Minimum number of weigh-ins needed in the LAST 14 DAYS before we trust a
 * weekly weight trend. This is the "minimal" bar the plan engine uses in
 * `analyzeWeightIns` (shared/src/plan-update.ts), which imports this constant.
 */
export const MIN_WEIGHINS_LAST_14D_FOR_TREND = 6;

/**
 * Decide whether there are enough weigh-ins to state a confident weekly
 * weight trend: enough in the last week, OR enough across two weeks.
 *
 * These are the SAME thresholds the plan engine applies in `analyzeWeightIns`
 * (shared/src/plan-update.ts) — both sides import the two constants above, so
 * the numbers can never drift. Note the WINDOW each side counts over is not
 * identical: the plan engine takes the last 14 weigh-ins by count
 * (`weightSamples.slice(-14)`), while the daily-card caller passes a 14-day
 * calendar window with one entry per day. So for a very sparse logger the two
 * can classify differently, which is intended — a weekly-rate DISPLAY should
 * be stricter about recent cadence than the plan engine is.
 *
 * Without this gate, two back-to-back weigh-ins get extrapolated into a
 * confident (and often wrong) weekly rate — see ticket #4306.
 *
 * @param samples Array of {x: daysFromEnd (0 = today, negative = older)}.
 *                Only `x` is read, so both {x} and {x, y} points work.
 */
export function hasEnoughWeighInsForWeightTrend(samples: { x: number }[]): boolean {
  // enough weigh-ins in the last 7 days (x > -7 means today back to 6 days ago)
  const enoughInLastWeek = samples.filter(s => s.x > -7).length >= MIN_WEIGHINS_LAST_7D_FOR_TREND;
  // or enough weigh-ins across the whole window we were given (14 days for the card)
  const enoughInTwoWeeks = samples.length >= MIN_WEIGHINS_LAST_14D_FOR_TREND;
  return enoughInLastWeek || enoughInTwoWeeks;
}

/**
 * What the daily weigh-in card should show for the weekly weight change.
 * - `Trend`        enough data and a meaningful change — show the rate.
 * - `Insufficient` too few weigh-ins to trust a trend — show an "undecided"
 *                  message instead of a number.
 * - `None`         nothing useful to say (fewer than 2 weigh-ins, or the
 *                  change is negligible) — show nothing.
 */
export enum WeightTrendKind {
  Trend = 'trend',
  Insufficient = 'insufficient',
  None = 'none',
}

/** Result of `estimateWeeklyWeightChangeForDisplay` (discriminated by `kind`). */
export type DisplayWeightTrend =
  | { kind: WeightTrendKind.Trend; weeklyChange_kg: number }
  | { kind: WeightTrendKind.Insufficient }
  | { kind: WeightTrendKind.None };

/**
 * Estimate the weekly body-weight change to DISPLAY on the daily weigh-in
 * card, using the same Theil-Sen fit the trend chart uses PLUS the plan
 * engine's minimum-sample gate (see `hasEnoughWeighInsForWeightTrend`).
 *
 * @param dailyWeights_kg One weight per day for the window (oldest first),
 *                        `undefined` for days with no weigh-in.
 * @returns A `DisplayWeightTrend` describing what the card should show.
 */
export function estimateWeeklyWeightChangeForDisplay(dailyWeights_kg: (number | undefined)[]): DisplayWeightTrend {
  // build the trend points, using negative days-from-end for x (0 = latest day)
  const points: { x: number; y: number }[] = [];
  for (let i = 0; i < dailyWeights_kg.length; i++) {
    const weight_kg = dailyWeights_kg[i];
    if (weight_kg !== undefined && !isNaN(weight_kg) && weight_kg > 0) {
      points.push({ x: i - (dailyWeights_kg.length - 1), y: weight_kg });
    }
  }

  // need at least 2 points to draw any line at all
  if (points.length < 2) {
    return { kind: WeightTrendKind.None };
  }

  // #4306: with too few weigh-ins, a line is meaningless — say "undecided"
  if (!hasEnoughWeighInsForWeightTrend(points)) {
    return { kind: WeightTrendKind.Insufficient };
  }

  // Theil-Sen slope and intercept (same math as cs-trend-chart)
  const slope = slopeEstimationPercentile(points, 0.5);
  const intercept = interceptEstimationPercentile(points, slope, 0.5);

  const startValue = roundToPrecision(slope * (-(dailyWeights_kg.length - 1)) + intercept, 0.1);
  const endValue = roundToPrecision(slope * 0 + intercept, 0.1);
  const weeklyChange_kg = roundToPrecision((endValue - startValue) / (dailyWeights_kg.length - 1) * 7, 0.1);

  // if the weekly change rounds to essentially nothing, there is nothing to show
  if (Math.abs(weeklyChange_kg) < 0.1) {
    return { kind: WeightTrendKind.None };
  }

  return { kind: WeightTrendKind.Trend, weeklyChange_kg };
}

/**
 * Estimate weight using Theil-Sen robust regression from weigh-in log entries.
 * Converts entries to samples and calls the core estimation function.
 *
 * The estimate is read off the trend line at 7 days ago, so with only a few
 * days of weigh-ins it lands before the first one and shows a weight the user
 * never had (#6015: 3 weigh-ins of 58.4 / 58.6 / 57.6 kg read as 60.0 kg).
 * It therefore applies the plan engine's minimum-sample gate, as the daily
 * card does since #4306 (see `hasEnoughWeighInsForWeightTrend`).
 * Passing that count gate is not enough on its own: 5 daily weigh-ins still
 * start only 5 days back, so the line read at 7 days ago lies before the first
 * one (#6050: 58.4 / 58.6 / 57.6 / 57.2 / 57.4 kg read as 59.2 kg). So it also
 * needs a weigh-in at or before the read point.
 * @param weighIns Array of weigh-in log entries
 * @param referenceDate Reference date for calculating days from end
 * @returns Estimated weight in kg, or undefined if there are too few weigh-ins to trust a trend
 */
export function estimateWeightFromWeighIns(weighIns: Log_WeighIn[], referenceDate: DateTime): number | undefined {
  const untilDate = referenceDate.endOf('day');
  const weightSamples: { x: number; y: number }[] = [];

  for (const weighIn of weighIns) {
    if (!weighIn.weight_kg || weighIn.weight_kg <= 0) continue;
    const entryDate = DateTime.fromISO(weighIn.time_utc).toLocal();
    const daysFromEnd = Math.round(entryDate.diff(untilDate, 'days').days);
    // filter to last 14 days
    if (daysFromEnd < -14 || daysFromEnd > 0) continue;
    weightSamples.push({ x: daysFromEnd, y: weighIn.weight_kg });
  }

  // if too few weigh-ins to trust a trend, return nothing so the caller shows the stored weight (#6015)
  if (!hasEnoughWeighInsForWeightTrend(weightSamples)) {
    return undefined;
  }

  // if no weigh-in reaches back to the read point, the line would be extended before the first one (#6050)
  if (!weightSamples.some(s => s.x <= -TREND_READ_DAYS_AGO)) {
    return undefined;
  }

  const result = estimateWeightFromSamples(weightSamples);
  return result?.estimatedBW_kg;
}
